import { usePathname } from "@web/hooks/use-pathname-compat";
import { cn } from "@web/lib/utils";
import { Bot, MessageSquare, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type NavDestination = {
  id: string;
  to: string;
  labelKey: string;
  labelFallback: string;
  icon: React.ComponentType<{ className?: string }>;
};

// The installed-app mobile navigation — the three Agent surfaces of the epic.
// Compte points at the existing settings screen for now (a dedicated mobile
// account screen is a design artifact, out of V0 scope).
const DESTINATIONS: NavDestination[] = [
  {
    id: "chats",
    to: "/ai",
    labelKey: "mobileNav.chats",
    labelFallback: "Chats",
    icon: MessageSquare,
  },
  {
    id: "proposals",
    to: "/ai-proposals",
    labelKey: "mobileNav.proposals",
    labelFallback: "Propositions",
    icon: Bot,
  },
  {
    id: "account",
    to: "/preferences",
    labelKey: "mobileNav.account",
    labelFallback: "Compte",
    icon: User,
  },
];

/**
 * Bottom navigation for the lean Agent app (V0). Rendered by AppSidebar
 * whenever the mobile-app experience is active — i.e. the Capacitor build, or
 * the website/PWA at a phone-sized viewport — matching the lean Agent router.
 *
 * A static flex child of MobileAppShell's column, NOT `fixed`: in-flow, the
 * content slot above shrinks by exactly the nav's height, so list rows and
 * page footers can never be hidden underneath it (no per-page bottom-padding
 * compensation). Half the home-indicator safe area is enough clearance —
 * the full inset left a visibly oversized gap under the labels.
 */
export function MobileAgentNav() {
  const pathname = usePathname();
  const { t } = useTranslation();

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label={t("mobileNav.label", "Navigation")}
      className="flex h-[calc(4rem+env(safe-area-inset-bottom)/2)] flex-shrink-0 border-t bg-background pb-[calc(env(safe-area-inset-bottom)/2)]"
    >
      {DESTINATIONS.map(({ id, to, labelKey, labelFallback, icon: Icon }) => {
        const active = pathname === to || pathname.startsWith(`${to}/`);
        return (
          <Link
            key={id}
            to={to}
            data-testid={`mobile-bottom-nav-${id}`}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex flex-1 flex-col items-center justify-center gap-0.5 text-xs transition-colors",
              active
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="size-5" />
            <span>{t(labelKey, labelFallback)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
